import React, { useEffect, useState } from 'react';
import {
  Alert, Box, Button, FormControlLabel, InputAdornment, Radio, RadioGroup, Skeleton, Stack, Switch, TextField, Typography,
  alpha, useTheme,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import financeService from '../../../services/financeService';
import { ENTITY_TYPE_OPTIONS, FinanceSettings } from '../../../types/finance';
import { PAYMENT_METHODS, PAYMENT_METHOD_LABEL } from '../../../utils/paymentMethods';
import SearchableSelect from '../../common/SearchableSelect';
import { useChart } from '../ledger/useChart';
import { useModulePermissions } from '../../../hooks/useModulePermissions';
import { SectionCard } from '../../common/SectionCard';
import { useAuth } from '../../../context/AuthContext';

const pick = (s: FinanceSettings) => ({
  entityType: s.entityType,
  requireJournalApproval: s.requireJournalApproval,
  paymentAccounts: s.paymentAccounts,
  writeOffAfterDays: s.writeOffAfterDays,
  approvalLimit: s.approvalLimit,
});

/** The school's legal form, where each payment method lands, and journal approval. */
const AccountingPreferencesPanel: React.FC = () => {
  const theme = useTheme();
  const { canWrite } = useModulePermissions();
  const { enqueueSnackbar } = useSnackbar();
  const [saved, setSaved] = useState<FinanceSettings | null>(null);
  const [draft, setDraft] = useState<FinanceSettings | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const editable = canWrite('finance_accounts');
  const { options } = useChart();
  const { currency } = useAuth();

  useEffect(() => {
    financeService.getSettings()
      .then(s => { setSaved(s); setDraft(s); })
      .catch((e: any) => setError(e?.message || 'Could not load accounting preferences.'));
  }, []);

  const dirty = !!saved && !!draft && JSON.stringify(pick(saved)) !== JSON.stringify(pick(draft));

  const save = async () => {
    if (!draft) return;
    setBusy(true);
    try {
      const s = await financeService.updateSettings(pick(draft));
      setSaved(s);
      setDraft(s);
      enqueueSnackbar('Accounting preferences saved', { variant: 'success' });
    } catch (e: any) {
      enqueueSnackbar(e?.message || 'Could not save preferences.', { variant: 'error' });
    } finally {
      setBusy(false);
    }
  };

  if (error) return <Alert severity="error">{error}</Alert>;
  if (!draft) return <Stack spacing={2}><Skeleton height={180} /><Skeleton height={90} /></Stack>;

  return (
    <Stack spacing={2.5}>
      <SectionCard title="Legal form">
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Decides how the equity accounts are named. Drawings are only for proprietors and partnerships.
        </Typography>
        <RadioGroup value={draft.entityType} onChange={e => setDraft({ ...draft, entityType: e.target.value as FinanceSettings['entityType'] })}>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.25 }}>
            {ENTITY_TYPE_OPTIONS.map(o => {
              const active = draft.entityType === o.value;
              return (
                <FormControlLabel
                  key={o.value}
                  value={o.value}
                  disabled={!editable}
                  control={<Radio size="small" />}
                  label={
                    <Box>
                      <Typography variant="body2" fontWeight={600}>{o.label}</Typography>
                      <Typography variant="caption" color="text.secondary">{o.hint}</Typography>
                    </Box>
                  }
                  sx={{
                    m: 0, px: 1.5, py: 1, alignItems: 'flex-start', borderRadius: 2, border: 1,
                    borderColor: active ? 'primary.main' : 'divider',
                    bgcolor: active ? alpha(theme.palette.primary.main, 0.04) : 'transparent',
                    '& .MuiRadio-root': { mt: -0.25 },
                  }}
                />
              );
            })}
          </Box>
        </RadioGroup>
      </SectionCard>

      <SectionCard title="Where money moves">
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          The cash or bank account each payment method uses — for fee receipts, fines, other income and expenses. Changing one affects new records only.
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
          {PAYMENT_METHODS.map(m => (
            <SearchableSelect
              key={m}
              label={PAYMENT_METHOD_LABEL[m]}
              value={draft.paymentAccounts?.[m] ?? ''}
              onChange={v => v && setDraft({ ...draft, paymentAccounts: { ...draft.paymentAccounts, [m]: v } })}
              options={options.cashAndBank}
              clearable={false}
              fullWidth
              minWidth={0}
              disabled={!editable}
            />
          ))}
        </Box>
      </SectionCard>

      <SectionCard title="Bad debts">
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
          <TextField
            label="Overdue before write-off"
            size="small"
            type="number"
            value={draft.writeOffAfterDays}
            onChange={e => setDraft({ ...draft, writeOffAfterDays: Math.max(0, Math.min(3650, parseInt(e.target.value || '0', 10))) })}
            InputProps={{ endAdornment: <InputAdornment position="end">days</InputAdornment> }}
            disabled={!editable}
            sx={{ width: 220 }}
          />
          <Typography variant="body2" color="text.secondary">
            A balance can be written off once it is this many days overdue — or at once when the student has left.
          </Typography>
        </Stack>
      </SectionCard>

      <SectionCard title="Second-person approval">
        <FormControlLabel
          disabled={!editable}
          control={<Switch checked={draft.requireJournalApproval} onChange={e => setDraft({ ...draft, requireJournalApproval: e.target.checked })} />}
          label={
            <Box>
              <Typography variant="body2" fontWeight={600}>A second person approves money decisions</Typography>
              <Typography variant="caption" color="text.secondary">
                Manual journals, refunds, write-offs, cash differences, reopening a month, expenses, and waivers or concessions above the limit below.
                Recommended: auditors expect the maker and the approver to be different people. Turn off if one person keeps the books — they can then do and approve everything themselves.
              </Typography>
            </Box>
          }
          sx={{ alignItems: 'flex-start', m: 0, '& .MuiSwitch-root': { mt: -0.5 } }}
        />
        {draft.requireJournalApproval && (
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }} sx={{ mt: 2, pl: { sm: 6 } }}>
            <TextField
              label="Approve waivers above"
              size="small"
              type="number"
              value={draft.approvalLimit}
              onChange={e => setDraft({ ...draft, approvalLimit: Math.max(0, Number(e.target.value || 0)) })}
              InputProps={{ endAdornment: <InputAdornment position="end">{currency}</InputAdornment> }}
              disabled={!editable}
              sx={{ width: 220 }}
            />
            <Typography variant="body2" color="text.secondary">
              Smaller waivers and flat concessions apply at once. Percentage concessions always need a second person.
            </Typography>
          </Stack>
        )}
      </SectionCard>

      {editable && (
        <Stack direction="row" justifyContent="flex-end" spacing={1}>
          <Button color="inherit" disabled={!dirty || busy} onClick={() => setDraft(saved)}>Discard</Button>
          <Button variant="contained" disableElevation disabled={!dirty || busy} onClick={save}>{busy ? 'Saving…' : 'Save preferences'}</Button>
        </Stack>
      )}
    </Stack>
  );
};

export default AccountingPreferencesPanel;
